import { useEffect, useRef, type ReactNode } from 'react';
import '../vendor/deck-stage';

interface DeckStageProps {
  children: ReactNode;
  width?: number;
  height?: number;
  noRail?: boolean;
  onSlideChange?: (detail: { index: number; total: number }) => void;
}

/**
 * React wrapper around the vendored <deck-stage> web component.
 * The component is fixed-position fullscreen while mounted (presenting mode);
 * `body.vc-presenting` lets print CSS hide the Terra shell around it.
 */
export function DeckStage({ children, width = 1920, height = 1080, noRail, onSlideChange }: DeckStageProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    document.body.classList.add('vc-presenting');
    return () => {
      document.body.classList.remove('vc-presenting');
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !onSlideChange) return;
    const handler = (e: Event) => {
      onSlideChange((e as CustomEvent).detail as { index: number; total: number });
    };
    el.addEventListener('slidechange', handler);
    return () => {
      el.removeEventListener('slidechange', handler);
    };
  }, [onSlideChange]);

  return (
    <deck-stage ref={ref} width={width} height={height} {...(noRail ? { 'no-rail': '' } : {})}>
      {children}
    </deck-stage>
  );
}
